import { useState } from "react";
import { toast } from "sonner";
import {
  useCreateEmailTemplateMutation,
  useEmailTemplatesQuery,
  useUpdateEmailTemplateMutation,
  type EmailTemplate,
} from "../api/queries/emailTemplateQueries";
import {
  useUpdateEscalationLevelMutation,
  type EscalationLevel,
} from "../api/queries/escalationQueries";
import {
  DEFAULT_INVOICE_LIST_FORMAT,
  STARTER_SINGLE_TEMPLATE_BODY,
  STARTER_SINGLE_TEMPLATE_SUBJECT,
} from "../utils/emailTemplatePreview";
import { emailTemplateToFormData } from "../utils/emailTemplateForm";
import {
  EmailTemplateModal,
  type TemplateFormData,
} from "./EmailTemplateModal";
import { EmailTemplatePreviewPanel } from "./EmailTemplatePreviewPanel";
import { LinkEmailTemplateModal } from "./LinkEmailTemplateModal";

const createDefaults = (stepLabel: string): TemplateFormData => ({
  name: `Escalation — ${stepLabel}`,
  subject: STARTER_SINGLE_TEMPLATE_SUBJECT,
  body: STARTER_SINGLE_TEMPLATE_BODY,
  trigger_type: "DUE_DATE",
  offset_days: 0,
  condition_balance: "ANY",
  audience_type: "SINGLE_INVOICE",
  invoice_list_format: DEFAULT_INVOICE_LIST_FORMAT,
  enabled: true,
  group_ids: [],
  repeat_interval_days: 5,
  invoice_inclusion_scope: "OVERDUE_OPEN",
  inclusion_min_days_overdue: 90,
});

interface EscalationStepTemplateSectionProps {
  level: EscalationLevel;
}

export function EscalationStepTemplateSection({
  level,
}: EscalationStepTemplateSectionProps) {
  const { data: templates = [] } = useEmailTemplatesQuery();
  const createTemplate = useCreateEmailTemplateMutation();
  const updateTemplate = useUpdateEmailTemplateMutation();
  const updateLevel = useUpdateEscalationLevelMutation();

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(
    null,
  );
  const [formData, setFormData] = useState<TemplateFormData>(
    createDefaults(`Step ${level.sequence_order}`),
  );

  const linkedTemplate = templates.find((t) => t.id === level.email_template_id);

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData(createDefaults(`Step ${level.sequence_order}`));
    setTemplateModalOpen(true);
  };

  const openEditModal = () => {
    if (!linkedTemplate) return;
    setEditingTemplate(linkedTemplate);
    setFormData(emailTemplateToFormData(linkedTemplate));
    setTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setTemplateModalOpen(false);
    setEditingTemplate(null);
  };

  const handleTemplateSubmit = () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error("Please fill in all required fields");
      return;
    }

    const templateData = {
      ...formData,
      invoice_list_format:
        formData.audience_type === "SINGLE_INVOICE"
          ? null
          : formData.invoice_list_format || DEFAULT_INVOICE_LIST_FORMAT,
    };

    if (editingTemplate) {
      updateTemplate.mutate(
        { templateId: editingTemplate.id, templateData },
        {
          onSuccess: () => {
            toast.success("Template updated");
            closeTemplateModal();
          },
          onError: () => toast.error("Failed to update template"),
        },
      );
      return;
    }

    createTemplate.mutate(templateData, {
      onSuccess: async (created) => {
        try {
          await updateLevel.mutateAsync({
            levelId: level.id,
            email_template_id: created.id,
          });
          toast.success("Template created and linked");
          closeTemplateModal();
        } catch {
          toast.error("Template created but failed to link to step");
        }
      },
      onError: () => toast.error("Failed to create template"),
    });
  };

  if (!linkedTemplate) {
    return (
      <>
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4">
          <p className="text-sm font-medium text-slate-700">
            No email template linked
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Link an existing template or create one for this escalation step.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setLinkModalOpen(true)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Link template
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Create template
            </button>
          </div>
        </div>

        <LinkEmailTemplateModal
          opened={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          levelId={level.id}
          currentTemplateId={level.email_template_id}
        />

        <EmailTemplateModal
          opened={templateModalOpen}
          onClose={closeTemplateModal}
          formData={formData}
          onFormDataChange={setFormData}
          onSubmit={handleTemplateSubmit}
          isLoading={createTemplate.isPending || updateLevel.isPending}
          isEditing={false}
        />
      </>
    );
  }

  return (
    <>
      <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Email template
            </p>
            <p className="mt-0.5 font-medium text-slate-900">
              {linkedTemplate.name}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setLinkModalOpen(true)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              Change template
            </button>
            <button
              type="button"
              onClick={openEditModal}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              Edit template
            </button>
          </div>
        </div>

        <EmailTemplatePreviewPanel
          subject={linkedTemplate.subject}
          body={linkedTemplate.body}
          previewMode="single"
        />
      </div>

      <LinkEmailTemplateModal
        opened={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        levelId={level.id}
        currentTemplateId={level.email_template_id}
      />

      <EmailTemplateModal
        opened={templateModalOpen}
        onClose={closeTemplateModal}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleTemplateSubmit}
        isLoading={updateTemplate.isPending}
        isEditing={Boolean(editingTemplate)}
      />
    </>
  );
}

export function escalationTemplateSummary(
  level: EscalationLevel,
  templates: EmailTemplate[],
): string {
  if (!level.email_template_id) {
    return "No email template";
  }
  const template = templates.find((t) => t.id === level.email_template_id);
  return template?.name ?? "Linked template";
}
